import React, { useState, useEffect } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { DragDropContext, Droppable, Draggable } from "react-beautiful-dnd";
import Swal from "sweetalert2";
import * as productPreSubManagement from "../../../services/productPreSubManagement";
import CustomPagination from "../../../common/custompagination";
import Sorting from "../../../common/sorting";
import Breadcrums from "../../../common/breadcrumbs";
import ProductManagementTabs from "../../../common/productManagementTabs";
import { SWAL_SETTINGS } from "../../../../../utils/Constants";
import { capitalizeFirstLetter, formateDateWithMonthAdmin, handleServerValidations, TrimText, usePermission } from "../../../../../utils/commonfunction";
import CustomRangepicker from "../../../common/rangepicker";
import { Placeholder, Tooltip, Whisper } from "rsuite";
import AssignAttributesModal from "./asignmodal.js";

const PreSubCategories = () => {
  const { can } = usePermission();
  const { t } = useTranslation();
  const params = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const [list, setList] = useState([]);
  const [statsupdate, setStatusUpdate] = useState("false");
  const [datalength, setDataLength] = useState("");
  const [itemperpage] = useState(10);
  const [sorting, setSorting] = useState({ column: 'position', order: 1 });
  const [defaultSorting, setDefaultSorting] = useState(true);
  const [page, setPage] = useState(1);
  const [loader, setLoader] = useState(true);
  const [isFetchingMore, setIsFetchingMore] = useState(false);
  const isFetchingRef = React.useRef(false);
  const [search, setSearch] = useState({});
  const [globalsearch, setGlobalSearch] = useState("");
  const breadcrumbs = [
    { title: t("sidebar_link_dashboard"), url: "/admin/dashboard" },
    { title: t("pre_sub_categories"), url: "" },
  ];
  const [resetdate, setResetDate] = useState(false);
  const [categories, setCategories] = useState([]);
  const [subCategories, setSubCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState("");
  const [selectedSubCategory, setSelectedSubCategory] = useState("");
  const [assignModal, setAssignModal] = useState({ open: false, data: null });

  useEffect(() => {
    import("../../../services/productManagement").then(productManagement => {
      productManagement.List({ page: 1, per_page: 1000 }).then(res => {
        if (res.success) setCategories(res.data.list);
      });
    });
    import("../../../services/productSubManagement").then(productSubManagement => {
      productSubManagement.List({ page: 1, per_page: 1000 }).then(res => {
        if (res.success) setSubCategories(res.data.list);
      });
    });
  }, []);


  useEffect(() => {
    const getData = setTimeout(() => {

      if (search) {
        if (page === 1) setLoader(true);
        else setIsFetchingMore(true);

        const formData = new FormData();
        formData.append("page", page);
        formData.append("per_page", itemperpage);
        formData.append("sort", JSON.stringify(sorting));
        formData.append("search", JSON.stringify(search));
        formData.append("global_search", globalsearch);
        if (selectedCategory) formData.append("categoryId", selectedCategory);
        if (selectedSubCategory) formData.append("subcategoryId", selectedSubCategory);
        productPreSubManagement.List(formData)
          .then((data) => {
            setDataLength(data.data.total_records);
            const newItems = data && data.data && data.data.list ? data.data.list : [];
            if (page === 1) {
              setList(newItems);
            } else {
              setList(prev => {
                const existingIds = new Set(prev.map(item => item._id));
                const uniqueNewItems = newItems.filter(item => !existingIds.has(item._id));
                return [...prev, ...uniqueNewItems];
              });
            }
            setLoader(false);
            setIsFetchingMore(false);
            isFetchingRef.current = false;
          })
          .catch((error) => {
            console.log("error ====> ", error);
            setLoader(false);
            setIsFetchingMore(false);
            isFetchingRef.current = false;
          });
      }

    }, 300);
    return () => clearTimeout(getData)

  }, [statsupdate, sorting, search, globalsearch, itemperpage, page, selectedCategory, selectedSubCategory]);

  useEffect(() => {
    setPage(1);
    setList([]);
  }, [sorting, search, globalsearch, statsupdate, location.pathname, selectedCategory, selectedSubCategory]);

  useEffect(() => {
    const handleScroll = () => {
      if (
        window.innerHeight + document.documentElement.scrollTop + 50 >=
        document.documentElement.scrollHeight
      ) {
        if (!loader && !isFetchingRef.current && list.length < datalength) {
           isFetchingRef.current = true;
           setIsFetchingMore(true);
           setPage(prev => prev + 1);
        }
      }
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, [loader, isFetchingMore, list.length, datalength]);

  function Deletefunction(data) {
    Swal.fire({
      customClass: "swal-wide",
      title: t("msg_are_you_sure"),
      text: t("you_not_be_able_to_revert_this"),
      icon: "warning",
      showCancelButton: true,
      cancelButtonText: t("btn_cancel"),
      confirmButtonColor: "#403fad",
      cancelButtonColor: "#f1388b",
      confirmButtonText: t("btn_yes_delete"),
    }).then((result) => {
      if (result.isConfirmed) {
        productPreSubManagement.Delete(data).then((response) => {
          setStatusUpdate(!statsupdate);
          if (response.success) {
            Swal.fire({
              icon: "success",
              text: response.message,
              ...SWAL_SETTINGS,
            });

          } else {
            Swal.fire({
              icon: "error",
              text: handleServerValidations(response),
              ...SWAL_SETTINGS,
            });
          }
        })
          .catch((error) => {
            console.log("deleteError");
          });
      }
    });
  }

  const handleSort = (e, column) => {
    setDefaultSorting(false);
    let sort = { order: 0, column: column };
    if (e.target.classList.contains("assc")) {
      sort.order = -1;
    } else {
      sort.order = 1;
    }
    setSorting(sort);
  };

  const prepareSearch = (key, value) => {
    let sr = { ...search };
    if (String(value).length > 0) {
      sr[key] = value;
    } else {
      delete sr[key];
    }
    setSearch(sr);
  };

  const resetFilter = (e) => {
    e.preventDefault();
    setGlobalSearch("");
    prepareSearch();
    setSearch({});
    setSelectedCategory("");
    setSelectedSubCategory("");
    setResetDate(!resetdate);
  };

  const viewfunction = (row) => {
    navigate(`/admin/productManagement/presubcategories/view/${row._id}`);
  };

  const goToEdit = (row) => {
    navigate(`/admin/productManagement/presubcategories/edit/${row._id}`);
  };

  const ChangeStatus = (data, Num) => {
    const formData = new FormData();
    formData.append("_id", JSON.stringify(data).replaceAll('"', ""));
    formData.append("status", Num);
    Swal.fire({
      customClass: "swal-wide",
      title: t("msg_are_you_sure"),
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#403fad",
      cancelButtonColor: "#f1388b",
      confirmButtonText: t("btn_yes"),
      cancelButtonText: t("btn_cancel")
    }).then((result) => {
      if (result.isConfirmed) {
        productPreSubManagement
          .Status(formData)
          .then((response) => {
            if (response.success) {
              Swal.fire({
                icon: "success",
                text: response.message,
                ...SWAL_SETTINGS,
              });
              setStatusUpdate(!statsupdate);
            } else {
              Swal.fire({
                icon: "error",
                text: handleServerValidations(response),
                ...SWAL_SETTINGS,
              });
            }
          })
          .catch((error) => {
            console.log("error===>");
          });
      }
    });
  };

  const handleDragEnd = (result) => {
    if (!result.destination) return;
    const sourceIndex = result.source.index;
    const destinationIndex = result.destination.index;
    if (sourceIndex === destinationIndex) return;

    const items = Array.from(list);
    const [reorderedItem] = items.splice(sourceIndex, 1);
    items.splice(destinationIndex, 0, reorderedItem);

    const reorderedList = items.map((item, index) => ({
      ...item,
      position: index
    }));

    setList(reorderedList);

    productPreSubManagement.Reorder({ items: reorderedList.map(item => ({ _id: item._id, position: item.position })) }).catch(() => {
      setStatusUpdate(!statsupdate); // revert on fail
    });
  };

  return (
    <>
      <Breadcrums data={breadcrumbs} />
      {/* <ProductManagementTabs /> */}
      <div className="animation_fade">
        <div className="card custom-card">
          <div className="card-body">
            <div className="d-flex justify-content-between align-items-center mb-4">
              <h6 className="main-content-label">
                {t("pre_sub_categories")}
              </h6>
              <div className="d-flex align-items-center">
                <div className="form-group mb-0 me-3">
                  <select
                    className="form-control"
                    value={selectedCategory}
                    onChange={(e) => setSelectedCategory(e.target.value)}
                  >
                    <option value="">Select Parent Category</option>
                    {categories.map((cat) => (
                      <option key={cat._id} value={cat._id}>
                        {cat.title}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="form-group mb-0 me-3">
                  <select
                    className="form-control"
                    value={selectedSubCategory}
                    onChange={(e) => setSelectedSubCategory(e.target.value)}
                  >
                    <option value="">Select Sub Category</option>
                    {subCategories.filter(sub => !selectedCategory || sub.categoryId === selectedCategory).map((sub) => (
                      <option key={sub._id} value={sub._id}>
                        {sub.title}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="form-group mb-0 me-3">
                  <div className="form-group mb-0 rangepicker_container filter_design">
                    <i className="fa fa-search calender_icon"></i>
                    <input
                      type="search"
                      className="form-control"
                      value={globalsearch}
                      placeholder={t("input_placeholder_search")}
                      onChange={(e) => {
                        setGlobalSearch(e.target.value);
                      }}
                    />
                  </div>
                </div>
                <div className="me-3">
                  <CustomRangepicker
                    GetDateRange={(e) => {
                      prepareSearch("createdAt", e);
                    }}
                    resetdate={resetdate}
                  />
                </div>

                <button type="reset" value="Reset" onClick={resetFilter} className="btn btn-warning float-right mr-2" >
                  {t("btn_reset_filter")} </button>
                {can("/admin/productManagement/categories/add") && (
                  <button
                    className="btn ripple btn-main-primary signbtn mr-2"
                    onClick={() =>
                      navigate(`/admin/productManagement/presubcategories/add`)
                    }
                  >
                    {t("btn_add_new")}
                  </button>
                )}
              </div>
            </div>

            {
              loader ?
                <Placeholder.Paragraph className="w-100" rowHeight={30} rowSpacing={5} active={true} rows={7} />
                :
                <>
                  <div className="table-responsive min-h-400">
                    <table className="table table-bordered border-t0 table table-bordered card-table border-top text-nowrap w-100 text-nowrap w-100">
                      <thead>
                        <tr>
                          <th className="position-relative select_head">
                            <span>{t("list_heading_sno")}</span>
                          </th>
                          <th>
                            <div className="rating_head">
                              <span>{t("list_heading_name")}</span>
                            </div>
                          </th>
                          <th>
                            <div className="rating_head">
                              <span>{t("sub_category")}</span>
                            </div>
                          </th>
                          <th>
                            <div className="rating_head">
                              <span>{t("parent_category")}</span>
                            </div>
                          </th>
                          <th className="status_head">{t("list_heading_status")}</th>
                          <th className="created_head">
                            <div className="sorting_column">
                              <span>{t("list_heading_created_date")}</span>
                              <Sorting
                                sort={sorting}
                                handleSort={handleSort}
                                defaultSorting={defaultSorting}
                                column="createdAt"
                              />
                            </div>
                          </th>
                          <th className="action_head">{t("list_heading_action")}</th>
                        </tr>
                      </thead>
                      <DragDropContext onDragEnd={handleDragEnd}>
                        <Droppable droppableId="droppable-presubcategories">
                          {(provided) => (
                            <tbody ref={provided.innerRef} {...provided.droppableProps}>
                              {list.length ? (
                                list.map((row, i) => (
                                  <Draggable key={row._id} draggableId={row._id} index={i}>
                                    {(provided, snapshot) => (
                                      <tr
                                        ref={provided.innerRef}
                                        {...provided.draggableProps}
                                        style={{
                                          ...provided.draggableProps.style,
                                          display: snapshot.isDragging ? "table" : "",
                                          backgroundColor: snapshot.isDragging ? "#f8f9fa" : ""
                                        }}
                                      >
                                        <td {...provided.dragHandleProps} style={{ cursor: "grab" }}>
                                          <i className="ri-drag-move-2-fill me-2" style={{ fontSize: "16px" }}></i>
                                          {row ? i + 1 : null}
                                        </td>
                                  <td>{row?.title ? <div className="d-flex">{row?.title ? capitalizeFirstLetter(TrimText(row.title, 50)) : "N/A"}</div> : <div className="d-flex">N/A</div>}</td>
                                  <td>{row?.subCategory ? <div className="d-flex">{capitalizeFirstLetter(row.subCategory)}</div> : <div className="d-flex">N/A</div>}</td>
                                  <td>{row?.parentCategory ? <div className="d-flex">{capitalizeFirstLetter(row.parentCategory)}</div> : <div className="d-flex">N/A</div>}</td>
                                  <td>
                                    <label className={"custom-switch cp" + (row.status !== 1 ? " e_none" : "")}>
                                      <input
                                        className="custom-switch-input cp"
                                        type="checkbox"
                                        checked={row?.status === 1}
                                        disabled={!can("/admin/productManagement/categories/edit")}
                                        onChange={(e) => can("/admin/productManagement/categories/edit") && ChangeStatus(row?._id, row?.status === 1 ? 0 : 1)}
                                      />
                                      <span className="custom-switch-indicator cp"></span>
                                    </label>
                                  </td>
                                  <td>{row.createdAt ? formateDateWithMonthAdmin(row.createdAt) : "N/A"}</td>
                                  <td>
                                    <div className="btn-toolbar table-actions" role="toolbar" aria-label="Toolbar with button groups">
                                      <div className="btn-group btn-group-sm" role="group" aria-label="Second group">
                                         <Whisper placement="top" controlId="control-id-hover" trigger="hover" speaker={<Tooltip style={{ fontWeight: "bold" }}>View</Tooltip>}>
                                      <button
                                        type="button"
                                        className="btn view"
                                        onClick={() => {
                                          viewfunction(row);
                                        }}>
                                        <i className="ri-eye-fill"></i>
                                      </button>
                                    </Whisper>
                                        {can("/admin/productManagement/categories/edit") && (
                                          <Whisper placement="top" controlId="control-id-hover" trigger="hover" speaker={<Tooltip style={{ fontWeight: 'bold' }}>Edit</Tooltip>}>
                                            <button type="button" className="btn edit" onClick={() => { goToEdit(row); }}>
                                              <i className="ri-pencil-fill"></i>
                                            </button>
                                          </Whisper>
                                        )}
                                        {can("/admin/productManagement/categories/delete") && (
                                          <Whisper placement="top" controlId="control-id-hover" trigger="hover" speaker={<Tooltip style={{ fontWeight: 'bold' }}>Delete</Tooltip>}>
                                            <button type="button" className="btn delete" onClick={() => { Deletefunction(row) }}>
                                              <i className="ri-delete-bin-6-fill"></i>
                                            </button>
                                          </Whisper>
                                        )}
                                        {can("/admin/productManagement/categories/edit") && (
                                          <Whisper placement="top" controlId="control-id-hover" trigger="hover" speaker={<Tooltip style={{ fontWeight: 'bold' }}>Assign Attributes</Tooltip>}>
                                            <button type="button" className="btn text-primary" onClick={() => { setAssignModal({ open: true, data: row }); }}>
                                              <i className="ri-settings-4-fill"></i>
                                            </button>
                                          </Whisper>
                                        )}
                                        </div>
                                    </div>
 </td>                                      </tr>
                                    )}
                                  </Draggable>
                                ))
                              ) : (
                                <tr>
                                  <td colSpan={10} className="text-center">
                                    {t("msg_no_records")}
                                  </td>
                                </tr>
                              )}
                              {provided.placeholder}
                            </tbody>
                          )}
                        </Droppable>
                      </DragDropContext>
                    </table>
                  </div>
                  <div className="" id="example1_info" role="status" aria-live="polite" >
                    <b> {t("msg_total_records")} : {datalength ? datalength : "0"} </b>
                  </div>
                  {isFetchingMore && (
                    <div className="text-center mt-3 mb-3">
                      <div className="spinner-border text-primary" role="status">
                        <span className="visually-hidden">Loading...</span>
                      </div>
                    </div>
                  )}
                </>
            }
          </div>
        </div>
      </div>
      <AssignAttributesModal
        open={assignModal.open}
        onClose={() => setAssignModal({ open: false, data: null })}
        preSubCategory={assignModal.data}
        onSuccess={() => setStatusUpdate(!statsupdate)}
      />
    </>
  );
};

export default PreSubCategories;
